import React, { Component, Fragment } from 'react';
import { View, TouchableOpacity, ScrollView, StyleSheet } from 'react-native';
import { observer, Observer } from 'mobx-react';

import Text from '../../components/shared/Text';
import Button from '../../components/shared/Button';
import Alert from '../../components/shared/Alert';
import Card, { WIDTH as CARD_WIDTH } from '../../components/shared/Card';
import Picker from '../../components/shared/Picker';

import { WINDOW_WIDTH, PADDING, BUTTON_HEIGHT } from '../../components/styleConstants';

import { getBuilding } from '../../utils/buildingUtils';
import { isDressable } from '../../utils/thingUtils';
import { getLocalValue, t, action } from '../../utils/appUtils';
import { getThingUsedCost, isThingSellable, itemCanBeBought } from '../../utils/warriorUtils';

import appStore from '../../stores/app';
import heroStore from '../../stores/hero';
import buildingData from '../../stores/buildingData';

import {
  filterThings,
  FilterPicker as ThingsFilterPicker,
  styles as inventoryStyles,
} from '../../components/Inventory/Inventory';
import PaymentForm from '../../components/shared/PaymentForm';

import ThingItem from '../../components/common/ThingItem';

import iapStore from '../../lib/iap';

import { THING_TYPES, BUILDING_MODULES } from '../../lib/constants';
import { IS_IOS, IS_WEB } from '../../lib/appConstants';

import { BuildingType, ThingType } from '../../lib/types';

import { buildingAction } from '../../lib/apiCalls';

import logImport from '../../lib/log';
const log = logImport as (name: string, params?: Record<string, any>) => void;
import { transaction, reaction } from 'mobx';

export const styles = StyleSheet.create({
  activeItem: {},
});

type Props = {
  id: string;
  sell?: boolean;
  dressable?: boolean;
  suppressFilter?: boolean;
};

type State = {
  tab: string;
  sellFilter: string | null;
  showPaymentForm: boolean;
  buyFilter: {
    type: string | null;
    level: number | string | null;
  };
};

const TAB_BUY = 'buy';
const TAB_SELL = 'sell';

export const THING_ITEM_WIDTH = WINDOW_WIDTH - 3 * PADDING - CARD_WIDTH;

@observer
export default class Shop extends Component<Props, State> {
  static defaultProps = {
    sell: true,
    dressable: true,
    suppressFilter: false,
  };

  state: State = {
    tab: TAB_BUY,
    sellFilter: null,
    showPaymentForm: false,
    buyFilter: {
      type: null,
      level: null,
    },
  };

  building: BuildingType;
  currentThing!: ThingType;
  disposeIapInit?: () => void;

  constructor(props: Props) {
    super(props);
    this.building = getBuilding(appStore.initData.buildings, props.id)!;
  }

  componentDidMount() {
    buildingData.fetch(this.building.id);

    // Init Apple IAP for the gold packs once the building data arrives. A
    // sellable thing may carry an `iapProductId` (an Apple IAP gold pack); the
    // buy buttons are rendered inline on those rows (see renderGoldPackButton).
    // iOS only.
    if (IS_IOS && !IS_WEB) {
      this.disposeIapInit = reaction(
        () => buildingData.data && buildingData.data.data.things,
        (things: { iapProductId?: string }[]) => {
          const productIds = (things || [])
            .map((item) => item.iapProductId)
            .filter(Boolean) as string[];
          if (productIds.length) {
            iapStore.init(productIds);
            this.disposeIapInit?.();
          }
        },
        { fireImmediately: true },
      );
    }
  }

  componentWillUnmount() {
    this.disposeIapInit?.();
  }

  onTab = (value: string) => {
    this.setState({ tab: value });
  };

  // Apple IAP buy button for a Scroll Gold row. The gold pack's IAP product id
  // lives inline on the building `things` entry (item.iapProductId) for the
  // `addMoney` scroll, so the $-priced pack buy sits next to the SOL crypto button.
  renderGoldPackButton(item: any, thing: any) {
    if (!IS_IOS || IS_WEB || thing.action !== 'addMoney' || !item.iapProductId) return null;
    const product = iapStore.getProduct(item.iapProductId);    
    if (!product) return null;
    return (
      <Button
        sizeObj={{ width: 150, height: BUTTON_HEIGHT }}
        disabled={iapStore.inFlight}
        onPress={() =>
          action(async () => {
            try {
              await iapStore.buy(item.iapProductId, {
                buildingId: this.building.id,
                packId: item.id,
              });
              appStore.showMessage({
                type: 'success',
                text: t('shop.thingWasBought'),
              });
            } catch (e) {
              // user cancelled or validation failed; iap.ts already logged it
            }
          })
        }
        style={{ marginTop: PADDING }}
        hitSlop={{ top: 0, bottom: 0 }}
      >
        {product.localizedPrice}
      </Button>
    );
  }

  renderBuyItems() {
    const { buyFilter } = this.state;
    const { dressable, suppressFilter } = this.props;
    const filterFn = (item: any) =>
      filterThings(
        appStore.initData.things,
        item.thing,
        buyFilter.type || (dressable ? THING_TYPES.SWORD : 'all'),
        (thing: any) =>
          buyFilter.level === null ||
          buyFilter.level === 'all' ||
          thing.levelNeed === buyFilter.level,
      );
    return buildingData.data.data.things
      .filter(suppressFilter ? () => true : filterFn)
      .map((item: any, index: number) => (
        <ThingItem
          width={THING_ITEM_WIDTH}
          key={item.thing}
          warrior={heroStore.hero}
          warriorThingOrId={item.thing}
          index={index}
          checkPrice
          actionsRenderer={(thing: any) => (
            <Observer>
              {() => (
                <View>
                  {thing.priceCrypto ? (
                    <Button
                      sizeObj={{
                        width: 150,
                        height: BUTTON_HEIGHT,
                      }}
                      disabled={!item.count}
                      onPress={() =>
                        action(() => {
                          this.currentThing = thing;
                          this.setState({ showPaymentForm: true });
                        })
                      }
                      style={{ marginTop: PADDING }}
                      hitSlop={{
                        top: 0,
                        bottom: 0,
                      }}
                    >
                      {t('buyFor', { price: 'SOL ' + thing.priceCrypto }).toUpperCase()}
                    </Button>
                  ) : null}
                  {this.renderGoldPackButton(item, thing)}
                  {thing.price ? (
                    <Button
                      sizeObj={{
                        width: 120,
                        height: BUTTON_HEIGHT,
                      }}
                      disabled={!item.count || !itemCanBeBought(heroStore.hero, thing)}
                      onPress={() =>
                        action(() => {
                          Alert.alert(
                            t('buyObjFor', {
                              name: getLocalValue(thing),
                              price: thing.price,
                            }),
                            t('areYouSure'),
                            [
                              { text: t('cancel'), style: 'cancel' },
                              {
                                text: t('yes'),
                                onPress: async () => {
                                  await buildingAction(
                                    this.building.id,
                                    BUILDING_MODULES.SHOP.ACTIONS.BUY,
                                    {
                                      thingId: thing.id,
                                    },
                                  );
                                  appStore.showMessage({
                                    type: 'success',
                                    text: t('shop.thingWasBought'),
                                  });
                                  log('BuyAction', {
                                    type: 'THING',
                                    id: thing.id,
                                    warriorLogin: heroStore.hero.login,
                                  });
                                },
                              },
                            ],
                          );
                        })
                      }
                      style={{ marginTop: PADDING }}
                      hitSlop={{
                        top: 0,
                        bottom: 0,
                      }}
                    >
                      {t('buyFor', { price: thing.price }).toUpperCase()}
                    </Button>
                  ) : null}

                  <Text bold style={{ marginTop: 5 }}>
                    {t('shop.countLeft', { count: item.count })}
                  </Text>
                </View>
              )}
            </Observer>
          )}
        />
      ));
  }

  renderSellItems() {
    const { sellFilter } = this.state;
    return heroStore.undressedThings
      .filter((item: any) =>
        filterThings(appStore.initData.things, item.thing, sellFilter, (thing: any) =>
          isDressable(thing),
        ),
      )
      .map((warriorThing: any, index: number) => (
        <ThingItem
          width={THING_ITEM_WIDTH}
          key={warriorThing.id}
          warrior={heroStore.hero}
          warriorThingOrId={warriorThing}
          index={index}
          actionsRenderer={(thing: any) => {
            const thingCost = getThingUsedCost(warriorThing, thing);
            return thingCost && isThingSellable(warriorThing) ? (
              <Button
                sizeObj={{
                  width: 145,
                  height: BUTTON_HEIGHT,
                }}
                onPress={() =>
                  action(() => {
                    Alert.alert(
                      `${t('shop.sellThingFor', {
                        name: getLocalValue(thing),
                      })} ${thingCost}`,
                      t('areYouSure'),
                      [
                        { text: t('cancel'), style: 'cancel' },
                        {
                          text: t('yes'),
                          onPress: async () => {
                            await buildingAction(
                              this.building.id,
                              BUILDING_MODULES.SHOP.ACTIONS.SELL,
                              {
                                warriorThingId: warriorThing.id,
                              },
                            );
                            log('SellAction', {
                              warriorThingId: warriorThing.id,
                              warriorLogin: heroStore.hero.login,
                            });
                            appStore.showMessage({
                              type: 'success',
                              text: t('shop.thingWasSold'),
                            });
                          },
                        },
                      ],
                    );
                  })
                }
                style={{ marginTop: PADDING }}
                hitSlop={{
                  top: 0,
                  bottom: 0,
                }}
              >
                {`${t('shop.sellThing').toUpperCase()} ${thingCost}`}
              </Button>
            ) : null;
          }}
        />
      ));
  }

  onPayed = (transactionId: string, solTransactionId: string) => {
    action(async () => {
      await buildingAction(this.building.id, BUILDING_MODULES.SHOP.ACTIONS.BUY_CRYPTO, {
        thingId: this.currentThing.id,
        transactionId,
        solTransactionId,
      });
    });
  };

  render() {
    if (!buildingData.data) return null;
    const { tab, sellFilter, buyFilter, showPaymentForm } = this.state;

    const { sell, dressable, suppressFilter } = this.props;
    const isSell = tab === TAB_SELL;

    const leveFilterData: { key: any; label: string }[] = [...Array(9).keys()].map((index) => ({
      key: index,
      label: String(index),
    }));
    leveFilterData.unshift({ key: 'all', label: t('all') });

    return (
      <View>
        <View style={{ width: WINDOW_WIDTH - CARD_WIDTH - 3 * PADDING }}>
          {!isSell ? (
            <Fragment>
              {!suppressFilter ? (
                <View style={{ flexDirection: 'row', height: BUTTON_HEIGHT }}>
                  <ThingsFilterPicker
                    key="buyFilterType"
                    value={buyFilter.type}
                    showInitPlaceholder
                    showAll={false}
                    filter={(key: any) => {
                      const isDdressable = isDressable({ type: key });
                      return dressable ? isDdressable : !isDdressable;
                    }}
                    onChange={(value: string) => {
                      this.setState({ buyFilter: { ...buyFilter, type: value } });
                    }}
                  />
                  <Picker
                    style={{ marginLeft: PADDING }}
                    data={leveFilterData}
                    placeholder={t('shop.level').toUpperCase()}
                    value={buyFilter.level}
                    onChange={(value: any) => {
                      this.setState({
                        buyFilter: { ...buyFilter, level: value },
                      });
                    }}
                  />
                </View>
              ) : null}
              <ScrollView style={inventoryStyles.container}>
                {this.renderBuyItems()}
              </ScrollView>
            </Fragment>
          ) : (
            <Fragment>
              <View style={{ height: BUTTON_HEIGHT }}>
                <ThingsFilterPicker
                  key="sellFilter"
                  value={sellFilter}
                  filter={(key: any) => isDressable({ type: key })}
                  onChange={(value: string) => {
                    this.setState({ sellFilter: value });
                  }}
                />
              </View>
              <ScrollView style={inventoryStyles.container}>{this.renderSellItems()}</ScrollView>
            </Fragment>
          )}
        </View>
        <Card
          title={getLocalValue(this.building)}
          menuItemsLength={2}
          style={{
            position: 'absolute',
            right: 0,
          }}
        >
          <View style={{ flexDirection: 'row' }}>
            <TouchableOpacity
              hitSlop={{
                top: 20,
                left: 20,
                bottom: 0,
                right: 60,
              }}
              onPress={() => this.onTab(TAB_BUY)}
            >
              <Text bold={!isSell} style={!isSell ? styles.activeItem : null}>
                {t('shop.buy')}
              </Text>
            </TouchableOpacity>
          </View>
          {sell ? (
            <View style={{ flexDirection: 'row', marginTop: 4 }}>
              <TouchableOpacity
                hitSlop={{
                  top: 0,
                  left: 20,
                  bottom: 20,
                  right: 60,
                }}
                onPress={() => this.onTab(TAB_SELL)}
              >
                <Text bold={isSell} style={isSell ? styles.activeItem : null}>
                  {t('shop.sell')}
                </Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </Card>
        {showPaymentForm ? (
          <PaymentForm
            onPayed={this.onPayed}
            thing={this.currentThing}
            onDismiss={() => {
              this.setState({ showPaymentForm: false });
            }}
          />
        ) : null}
      </View>
    );
  }
}
